import { Routes, Route, Navigate } from 'react-router-dom';

import { Layout, RequireAuth } from '../components';

import {
  Welcome,
  Login,
  ResetPassword,
  Appointments,
  ScheduledAppointments,
  Clients,
  Items,
  Logs,
  Properties,
  Proposals,
  JobOverviewTemplates,
  Settings,
  Profile,
  Property,
  Client,
  Proposal,
  Page404,
  Page401,
  ProposalView,
  ProposalPublicView,
  Appointment,
  Schedule,
  Timer,
  Support,
  Archive,
  Mailer,
  DataImport,
  PrintBatch,
  PrintReceipt,
  ProposalEmbed,
  ProposalSmsView,
  ApiIntegrations,
  Installations,
  CancelledAppointments,
  EquipmentCatalog,
  Presentation,
  WarrantyTickets,
  WarrantyTicketCreate,
  WarrantyTicketDetail,
  LowPriceRequests,
  WaitList,
  Receivables,
} from '../pages';

import AppointmentCreate from '../pages/AppointmentCreate';

import permissions from '../static/permissions';

export const useRoutes = (isAuthenticated, userData) => {
  if (isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route
            path="/"
            element={userData?.role === 'technician' ? <Navigate to="/schedule" /> : <Navigate to="/appointments" />}
          />

          <Route path="/welcome" element={<Welcome />} />
          <Route path="/404" element={<Page404 />} />
          <Route path="/401" element={<Page401 />} />

          <Route path="/profile" element={<Profile />} />

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.matrix} />}
          >
            <Route path="appointments" element={<Appointments />} />
            <Route path="appointments/create" element={<AppointmentCreate />} />
          </Route>

          {/* Scheduled (deferred call) appointments: "schedule" permission so salesmen have access */}
          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.schedule} />}
          >
            <Route path="scheduled-appointments" element={<ScheduledAppointments />} />
          </Route>


          {/* Cancelled Appointments - only for admin, dispatcher */}
          {['superadmin', 'admin', 'dispatcher'].includes(userData?.role) && (
            <Route path="appointments/cancelled" element={<CancelledAppointments />} />
          )}
          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.schedule} />}
          >
            <Route path="appointments/:id" element={<Appointment />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.installations} />}
          >
            <Route path="installations" element={<Installations />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.clients} />}
          >
            <Route path="clients" element={<Clients />} />
            <Route path="clients/:id" element={<Client />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.items} />}
          >
            <Route path="items" element={<Items />} />
            <Route path="items/:tab" element={<Items />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.proposals} />}
          >
            <Route path="equipment-catalog" element={<EquipmentCatalog />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.properties} />}
          >
            <Route path="properties" element={<Properties />} />
            <Route path="properties/:id" element={<Property />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.proposals} />}
          >
            <Route path="proposals" element={<Proposals />} />
            <Route path="proposals/:id" element={<Proposal />} />
            <Route path="job-overview-templates" element={<JobOverviewTemplates />} />
          </Route>

          {/* Receivables dashboard: 'payments' permission (Square payment requests) */}
          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.payments} />}
          >
            <Route path="receivables" element={<Receivables />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.settings} />}
          >
            <Route path="settings" element={<Settings />} />
            <Route path="settings/:tab" element={<Settings />} />
          </Route>

          {/* Low-price contract review: Emergency contact users only */}
          {(userData?.emergencyContact || userData?.role === 'superadmin') && (
            <Route path="low-price-requests" element={<LowPriceRequests />} />
          )}

          <Route element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.logs} />}>
            <Route path="logs" element={<Logs />} />
            <Route path="logs/:tab" element={<Logs />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.schedule} />}
          >
            <Route path="schedule" element={<Schedule />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.timer} />}
          >
            <Route path="time-tracking" element={<Timer />} />
          </Route>

          <Route
            element={<RequireAuth userPermissions={userData?.permissions} allowedPermission={permissions.schedule} />}
          >
            <Route path="support" element={<Support />} />
          </Route>

          {/* Archive - only for superadmin, admin, dispatcher */}
          {['superadmin', 'admin', 'dispatcher'].includes(userData?.role) && (
            <Route path="archive" element={<Archive />} />
          )}

          {/* Mailer - only for superadmin */}
          {userData?.role === 'superadmin' && (
            <Route path="mailer" element={<Mailer />} />
          )}

          {/* Data Import - only for superadmin */}
          {userData?.role === 'superadmin' && (
            <Route path="data-import" element={<DataImport />} />
          )}

          {/* API Integrations - only for superadmin */}
          {userData?.role === 'superadmin' && (
            <Route path="api-integrations" element={<ApiIntegrations />} />
          )}

          {/* Presentation - superadmin or PRO users */}
          {(userData?.role === 'superadmin' || userData?.proVersion) && (
            <Route path="presentation" element={<Presentation />} />
          )}

          {/* Warranty Tickets - technician, dispatcher, admin, superadmin */}
          {['superadmin', 'admin', 'dispatcher', 'technician'].includes(userData?.role) && (
            <>
              <Route path="warranty-tickets" element={<WarrantyTickets />} />
              <Route path="warranty-tickets/create" element={<WarrantyTicketCreate />} />
              <Route path="warranty-tickets/:id" element={<WarrantyTicketDetail />} />
            </>
          )}

          {/* Wait List - admins or users with the wait_list permission */}
          {(['superadmin', 'admin'].includes(userData?.role) ||
            userData?.permissions?.includes(permissions.waitList)) && (
            <Route path="wait-list" element={<WaitList />} />
          )}
        </Route>
        <Route path="proposals/print-batch" element={<PrintBatch />} />
        <Route path="proposals/view/:id" element={<ProposalView />} />
        <Route path="proposals/public/:token" element={<ProposalPublicView />} />
        <Route path="proposals/receipt/:id" element={<PrintReceipt />} />
        <Route path="proposals/embed/:publicId" element={<ProposalEmbed />} />
        <Route path="c/:token" element={<ProposalSmsView />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="login" element={<Login />} />
      <Route path="reset-password" element={<ResetPassword />} />
      <Route path="proposals/print-batch" element={<PrintBatch />} />
      <Route path="proposals/view/:id" element={<ProposalView />} />
      <Route path="proposals/public/:token" element={<ProposalPublicView />} />
      <Route path="proposals/receipt/:id" element={<PrintReceipt />} />
      <Route path="proposals/embed/:publicId" element={<ProposalEmbed />} />
      <Route path="c/:token" element={<ProposalSmsView />} />
      <Route path="/404" element={<Page404 />} />
      <Route path="/401" element={<Page401 />} />

      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
};
